import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import { isObject } from 'underscore';
import classNames from 'classnames';

import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import { ActionEvent, ActionTargetType, ActionType, AnnouncementDB, BlProfile, EAnnouncementTypes, IAnnouncementMultiLanguageSettings, BrandliveEvent } from '../../../types/working-model';
import { OptionalComponent } from '../../../utils/optional-component';
import { getCurrentPageSourceInfo, useTracking } from '../../../utils/tracking';
import Icon, { ICONS } from '../../general-ui/icon';
import { ParamsProps } from '../../live-event/live-event';
import { getDefaultLanguage } from '../utils';
import usePrevious from '../../../utils/use-previous';
import { useIsSessionPage, useIsSingleSessionPage } from '../../../hooks/path.hooks';
import { useGetSession } from '../../../hooks/session.hooks';
import useSetBannerHeight from './use-banner-height-setter';
import { checkScheduleIsActive } from './check-schedule-is-active';

import './announcements-v2.scss';
import { EditorSizes } from '@/types/template-layouts';
import { selectEditorNavV3 } from '@/store/selectors/feature-flags';

const getMultiLanguageText = (messageTextMultiLanguage: IAnnouncementMultiLanguageSettings, currentlySelectedLanguage: string, baseLanguage: string) => {
	const messageText = messageTextMultiLanguage.messageText[currentlySelectedLanguage]?.text ?? '';
	const languageIsHidden = messageTextMultiLanguage.messageText[currentlySelectedLanguage]?.hidden ?? true;
	const baseMessageText = messageTextMultiLanguage.messageText[baseLanguage]?.text ?? '';
	const changedMessageText = messageTextMultiLanguage.messageText[currentlySelectedLanguage]?.changed ?? ''; // only non base can be changed (custom translation)

	const buttonText = messageTextMultiLanguage.buttonText[currentlySelectedLanguage]?.text ?? '';
	const baseButtonText = messageTextMultiLanguage.buttonText[baseLanguage]?.text ?? '';
	const changedButtonText = messageTextMultiLanguage.buttonText[currentlySelectedLanguage]?.changed ?? '';

	return {
		messageText: languageIsHidden
			? baseMessageText
			: changedMessageText
				? changedMessageText
				: messageText
					? messageText
					: baseMessageText,
		buttonText: languageIsHidden
			? baseButtonText
			: changedButtonText
				? changedButtonText
				: buttonText
					? buttonText
					: baseButtonText
	};
};

const AnnouncementsV2 = (): JSX.Element => {
	const { trackEvent } = useTracking();
	const { language: currentlySelectedLanguage } = useParams() as unknown as ParamsProps;
	const session = useGetSession();
	const isSessionPage = useIsSessionPage();
	const isSingleSessionPage = useIsSingleSessionPage();
	const blProfileUserToken = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);
	const blProfileUser = useTypedSelector(state => state.LiveEventReducer.blProfileUser);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);

	const event = eventBundle || workingEvent;
	const workingEventUuid = workingEvent?.uuid;

	const isMobileEditor = !!(workingEventUuid && editorSize === EditorSizes.mobile);
	const isTabletEditor = !!(workingEventUuid && editorSize === EditorSizes.tablet);

	const isV3 = useTypedSelector(selectEditorNavV3);
	const isV3Editor = !!(workingEventUuid && isV3);

	const announcements = useMemo(() => event?.announcements || [], [event?.announcements]);

	// There SHOULD only be one banner announcement per event
	const banner = useMemo(() => announcements.find(a => a.display_settings?.type === 'banner'), [announcements]);
	const prevBanner = usePrevious(banner);
	const [bannerActive, setBannerActive] = useState(banner?.schedule_on || banner?.expiration_time
		? checkScheduleIsActive(String(banner?.schedule_date || ''), String(banner?.expiration_time || ''))
		: true
	);

	const eventLanguages: string[] = useMemo(() =>
		// Look for this in sessions previews so announcements properly display before registration
		Array.from(new Set(event?.sessionsPreviews?.flatMap(session => session.languages) ?? [])),
		[event]
	);
	const eventIsMultiLanguage = eventLanguages.length > 1;

	useEffect(() => {
		// Don't flash the banner if they change it to a new one
		if (banner && prevBanner && (banner.id !== prevBanner.id || banner.schedule_on !== prevBanner.schedule_on)) {
			setBannerActive(false);
		}
	}, [banner, prevBanner]);

	const bannerRef = useRef<AnnouncementDB>(undefined);
	useEffect(() => {
		bannerRef.current = banner;
	}, [banner]);

	useEffect(() => {
		const banner = bannerRef.current;
		if (!banner) return;

		const intervalId = setInterval(() => {
			if (!checkScheduleIsActive(String(banner.schedule_date || ''), String(banner.expiration_time || ''))) {
				// Check the dates for the scheduled announcement
				setBannerActive(false);
			} else if (banner.expiration_time && String(banner.expiration_time) < new Date().toISOString()) {
				// Immediate, but the set duration has passed
				setBannerActive(false);
			} else {
				setBannerActive(true);
			}
		}, 2000);

		return () => clearInterval(intervalId);
	}, [banner, workingEvent]);

	useEffect(() => {
		if (banner?.display_settings?.textColor) {
			// <Icon /> doesn't like custom colors
			document.querySelector('#banner-announcement-button-link > svg > path')?.setAttribute('fill', banner.display_settings?.textColor);
		}
	}, [banner, banner?.display_settings?.textColor, bannerActive]);

	if (!banner || !bannerActive || !event) {
		return <div className="banner-announcement-placholder"></div>;
	}

	if (banner.recipients === EAnnouncementTypes.registered) {
		const isRegistered = event.registration_on && blProfileUserToken;

		if (!isRegistered) return <div className="banner-announcement-placholder"></div>;
	}

	if (banner.recipients === EAnnouncementTypes.session) {
		if (!session || !(isSessionPage || isSingleSessionPage)) {
			return <div className="banner-announcement-placholder"></div>;
		}

		if (!banner.to_sessions.includes(session.session)) {
			return <div className="banner-announcement-placholder"></div>;
		}
	}

	return (
		<AnnouncementsV2Ui
			banner={banner}
			trackEvent={trackEvent}
			currentlySelectedLanguage={currentlySelectedLanguage}
			blProfileUser={blProfileUser}
			event={event}
			eventIsMultiLanguage={eventIsMultiLanguage}
			isMobileEditor={isMobileEditor}
			isTabletEditor={isTabletEditor}
			isV3Editor={isV3Editor}
		/>
	);
};

type AnnouncementProps = {
	banner: AnnouncementDB;
	trackEvent: (event: any) => void;
	currentlySelectedLanguage: string;
	blProfileUser: BlProfile | null;
	event: BrandliveEvent;
	eventIsMultiLanguage: boolean;
	isMobileEditor?: boolean;
	isTabletEditor?: boolean;
	isV3Editor?: boolean;
};
const AnnouncementsV2Ui = ({
	banner,
	trackEvent,
	currentlySelectedLanguage,
	blProfileUser,
	event,
	eventIsMultiLanguage,
	isMobileEditor,
	isTabletEditor,
	isV3Editor,
}: AnnouncementProps) => {

	const { source_id, source_type } = useMemo(() => getCurrentPageSourceInfo(location.pathname, event, blProfileUser?.bl_profile || 0), [blProfileUser?.bl_profile, event]);
	const announcements = useMemo(() => event?.announcements || [], [event?.announcements]);

	const baseLanguage = useMemo(() => getDefaultLanguage(event), [event]);

	// callback ref rather than useRef so the measurement starts as soon as the node commits
	const [bannerEl, setBannerEl] = useState<HTMLDivElement | null>(null);
	useSetBannerHeight({ element: bannerEl, hasAnnouncements: !!(announcements && banner) });

	const { messageText, buttonText } = useMemo(() => {
		if (eventIsMultiLanguage) {
			return getMultiLanguageText(banner.message_text_multi_language, currentlySelectedLanguage, baseLanguage);
		} else {
			let msgText = '';
			let btnText = '';
			if (isObject(banner.message_text)) {
				msgText = (banner?.message_text?.[baseLanguage] || banner?.message_text?.base || '') as string;
			} else {
				msgText = banner.message_text;
			}

			if (isObject(banner.button_text)) {
				btnText = (banner?.button_text?.[baseLanguage] || banner?.button_text?.base || '') as string;
			} else {
				btnText = banner.button_text;
			}
			return { messageText: msgText, buttonText: btnText };
		}
	}, [banner.button_text, banner.message_text, banner.message_text_multi_language, baseLanguage, currentlySelectedLanguage, eventIsMultiLanguage]);

	return useMemo(() => (
		<div
			ref={setBannerEl}
			className={classNames('banner-announcement', {
				'editor-mobile': isMobileEditor,
				'editor-tablet': isTabletEditor,
				'v3-editor': isV3Editor,
			})}
			style={{ background: banner.display_settings?.backgroundColor }}
		>
			<div className="banner-announcement-text" style={{ color: banner.display_settings?.textColor }}>
				{messageText}
			</div>

			<OptionalComponent display={banner.button_on}>
				<a
					onClick={() => trackEvent({
						action: ActionEvent.Click,
						action_type: ActionType.Active,
						target_type: ActionTargetType.Announcement,
						source_id,
						source_type,
						target_id_string: banner.button_link,
						current_language: currentlySelectedLanguage,
						miscellaneous: JSON.stringify({ baseLanguage, messageText, email: blProfileUser?.email || '' })
					})}
					className="banner-announcement-button"
					href={banner.button_link}
					rel="noreferrer"
					target={banner.button_link_same_tab ? '' : '_blank'}
					id="banner-announcement-button-link"
					style={{
						'--announcement-color': banner.display_settings?.textColor
					} as CSSProperties}
				>
					<span>
						{buttonText}
					</span>
					<Icon name={ICONS.ARROW_RIGHT} size={12} color="" />
				</a>
			</OptionalComponent>
		</div>
	), [
		banner.button_link,
		banner.button_link_same_tab,
		banner.button_on,
		banner.display_settings?.backgroundColor,
		banner.display_settings?.textColor,
		baseLanguage,
		blProfileUser?.email,
		buttonText,
		currentlySelectedLanguage,
		isMobileEditor,
		isTabletEditor,
		isV3Editor,
		messageText,
		source_id,
		source_type,
		trackEvent,
	]);
};

export default AnnouncementsV2;
